"use client";

import { createContext, useContext, useEffect, useId, useRef, useState, useSyncExternalStore } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { eur, eurCentimes } from "@/lib/tools/explanations";
import { useSourcesVerifiees } from "./SourcesVerifiees";

/**
 * Chaque chiffre dit d'où il vient.
 *
 * UN MONTANT SOULIGNÉ EN POINTILLÉS SE DÉPLIE. Pas de fenêtre qui flotte au
 * survol : sur un téléphone, il n'y a pas de survol, et une bulle qui masque
 * la ligne d'en dessous se lit mal. Le calcul s'ouvre sous la ligne, dans le
 * fil de la lecture, et se referme d'un second clic. Un seul calcul ouvert à
 * la fois : en ouvrir un autre ferme le premier.
 *
 * DEUX PARTIES, DANS CET ORDRE. Le calcul d'abord, étape par étape, jusqu'au
 * montant affiché ; puis d'où viennent les taux, avec le lien vers la page
 * officielle et, quand la veille l'a retrouvée, la date de cette vérification :
 * la même que « Informations vérifiées le » sous l'outil. Sans veille, pas de
 * date plutôt qu'une date que personne n'a vérifiée. Les étapes et les sources sont construites
 * par lib/tools/explanations.js, et npm run check:explanations vérifie que
 * chaque étape retombe juste.
 */

const TEXTES = {
    fr: {
        indice: "Appuyez sur un montant pour voir son calcul et d'où il vient.",
        calcul: "Le calcul",
        sources: "D'où viennent ces chiffres",
        verifie: "Vérifié le {date}",
        jours: "jours",
        trimestre: "trimestre",
        trimestres: "trimestres",
        abonne: "abonné",
        abonnes: "abonnés",
        deuxPoints: "\u00A0: ",
    },
    en: {
        indice: "Tap an amount to see how it is worked out and where it comes from.",
        calcul: "How it is worked out",
        sources: "Where these figures come from",
        verifie: "Checked on {date}",
        jours: "days",
        trimestre: "quarter",
        trimestres: "quarters",
        abonne: "subscriber",
        abonnes: "subscribers",
        deuxPoints: ": ",
    },
};

const dateLisible = (iso, lang) =>
    new Date(`${iso}T12:00:00Z`).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });

/** Un seul calcul ouvert à la fois, par outil. */
export function useExplications() {
    const [ouverte, setOuverte] = useState(null);
    return {
        ouverte,
        basculer: (cle) => setOuverte((o) => (o === cle ? null : cle)),
    };
}

/**
 * Une phrase à trous dont certains trous sont des éléments : un montant
 * cliquable reste dans le fil de la phrase, là où la traduction le place.
 */
export function phraseAvecMontants(texte, valeurs) {
    return texte.split(/(\{\w+\})/).map((morceau, i) => {
        const cle = morceau.match(/^\{(\w+)\}$/)?.[1];
        return <span key={i}>{cle && cle in valeurs ? valeurs[cle] : morceau}</span>;
    });
}

/** La phrase qui dit que les montants se déplient. */
export function IndiceExplication({ lang = "fr" }) {
    const T = TEXTES[lang] || TEXTES.fr;
    return (
        <p className="mt-4 mb-0 flex items-center gap-1.5 text-xs text-gray-500 dark:text-lightBlue/70">
            <SolarIcon name="info-circle-linear" className="h-4 w-4 shrink-0" />
            {T.indice}
        </p>
    );
}

/**
 * LA NOTE DE CALCUL REPREND CE QUE L'OUTIL MONTRE. Chaque montant expliqué
 * s'inscrit ici tant qu'il est affiché, dans l'ordre où il apparaît : le
 * bouton « Télécharger la note de calcul » relit ce registre au moment du
 * clic. Rien n'est à déclarer outil par outil, et un montant qui disparaît de
 * l'écran disparaît aussi de la note.
 *
 * Un registre plutôt qu'un état : s'inscrire à chaque rendu ne doit rien
 * faire re-rendre.
 */
const Collecte = createContext(null);

/**
 * Qui veut savoir quand le registre change : le bouton de la note, qui ne
 * s'affiche qu'une fois au moins un montant explicable à l'écran. Un
 * abonnement plutôt qu'un état du collecteur, qui ferait re-rendre tout
 * l'outil à chaque montant monté.
 */
const Abonnements = createContext(null);

export function CollecteurExplications({ children }) {
    const registre = useRef(new Map());
    const abonnes = useRef(new Set());
    const [abonnements] = useState(() => ({
        abonner: (rappel) => {
            abonnes.current.add(rappel);
            return () => abonnes.current.delete(rappel);
        },
        prevenir: () => abonnes.current.forEach((rappel) => rappel()),
    }));
    return (
        <Collecte.Provider value={registre}>
            <Abonnements.Provider value={abonnements}>{children}</Abonnements.Provider>
        </Collecte.Provider>
    );
}

export const useCollecteExplications = () => useContext(Collecte);

const sansAbonnement = () => () => {};

/** Le nombre de montants explicables affichés, 0 au rendu serveur. */
export function useNombreExplications() {
    const registre = useContext(Collecte);
    const abonnements = useContext(Abonnements);
    return useSyncExternalStore(
        abonnements?.abonner ?? sansAbonnement,
        () => registre?.current.size ?? 0,
        () => 0,
    );
}

/** Le montant, rendu cliquable quand il a une explication. */
export function MontantExplicable({ explication, ouvert, onBasculer, children, className = "" }) {
    const registre = useContext(Collecte);
    const abonnements = useContext(Abonnements);
    const id = useId();
    useEffect(() => {
        if (!registre) return;
        const avant = registre.current.size;
        if (explication) registre.current.set(id, explication);
        else registre.current.delete(id);
        if (registre.current.size !== avant) abonnements?.prevenir();
    }, [registre, abonnements, id, explication]);
    useEffect(
        () => () => {
            if (registre?.current.delete(id)) abonnements?.prevenir();
        },
        [registre, abonnements, id],
    );

    if (!explication) return <span className={className}>{children}</span>;
    return (
        <button
            type="button"
            onClick={onBasculer}
            aria-expanded={ouvert}
            className={`${className} cursor-pointer underline decoration-dotted decoration-gray-400 underline-offset-4 transition-colors hover:decoration-primary ${
                ouvert ? "decoration-primary" : ""
            }`}
        >
            {children}
        </button>
    );
}

/** Le calcul déplié, puis ses sources. */
export function PanneauExplication({ explication, lang = "fr" }) {
    const { sources: catalogue, dates } = useSourcesVerifiees();
    if (!explication) return null;
    const T = TEXTES[lang] || TEXTES.fr;
    // Les sources de l'outil, envoyées par sa page : check:explanations
    // vérifie que chaque source citée en fait partie.
    const sources = [...new Set(explication.sources)].filter((cle) => catalogue[cle]).map((cle) => ({ cle, ...catalogue[cle] }));
    const valeur = (e) => {
        if (e.unite === "jours") return `${e.valeur} ${T.jours}`;
        // « 0 trimestre » en français, « 0 quarters » en anglais.
        if (e.unite === "trimestres") return `${e.valeur} ${(lang === "fr" ? e.valeur >= 2 : e.valeur !== 1) ? T.trimestres : T.trimestre}`;
        if (e.unite === "centimes") return eurCentimes(e.valeur, lang);
        if (e.unite === "abonnes") return `${e.valeur.toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} ${e.valeur > 1 ? T.abonnes : T.abonne}`;
        return eur(e.valeur, lang);
    };

    return (
        <div className="mt-2 mb-1 rounded-lg border border-blue-200/70 bg-blue-50 dark:border-blue-400/20 dark:bg-blue-400/[0.06] px-3.5 py-3 text-xs leading-5">
            <div className="font-semibold text-gray-700 dark:text-white">{T.calcul}</div>
            <ol className="mt-1.5 space-y-2">
                {explication.etapes.map((e, i) => (
                    <li key={`${e.libelle}-${i}`}>
                        <div className="text-gray-500 dark:text-lightBlue/70">{e.libelle}</div>
                        <div className="tabular-nums text-gray-800 dark:text-white">
                            {/* Un chiffre qui sort d'un barème entier se lit en
                                phrase ; une opération, en égalité. */}
                            {e.operation?.type === "moteur" ? `${e.calcul}${T.deuxPoints}` : `${e.calcul} = `}
                            <strong className="font-semibold">{valeur(e)}</strong>
                        </div>
                    </li>
                ))}
            </ol>
            {sources.length > 0 && (
                <>
                    <div className="mt-3 font-semibold text-gray-700 dark:text-white">{T.sources}</div>
                    <ul className="mt-1.5 space-y-1.5">
                        {sources.map((s) => (
                            <li key={s.libelle} className="text-gray-600 dark:text-lightBlue">
                                {s.libelle}
                                {s.liens.map((l) => (
                                    <span key={l.url}>
                                        {" · "}
                                        {/* Une couleur explicite, au contraste suffisant
                                            sur le fond bleu : sans classe, le lien prenait
                                            l'orange clair de la règle globale. */}
                                        <a
                                            href={l.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#B83C12] dark:text-[#FF8A5B] underline underline-offset-2 hover:!text-[#B83C12] dark:hover:!text-[#FF8A5B] hover:decoration-2"
                                        >
                                            {l.site} <span aria-hidden="true">↗</span>
                                        </a>
                                    </span>
                                ))}
                                {dates[s.cle] && (
                                    <span className="text-gray-500 dark:text-lightBlue/70">
                                        {" · "}
                                        {T.verifie.replace("{date}", dateLisible(dates[s.cle], lang))}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}
